import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowClockwise,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeSlash,
  Info,
  PencilSimple,
  WarningCircle,
} from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';

import {
  Button as DskButton,
  Checkbox as DskCheckbox,
  Field as DskField,
  Input as DskInput,
} from '../../components/desktop';
import { Checkbox as DsCheckbox, Cta, Input as DsInput, OtpCells } from '../../components/mobile';

import './auth.css';

/**
 * The auth form pattern. Nine routes share it: login, signup, forgot password,
 * two OTP screens, reset password, and the two admin login steps.
 *
 * WHY THESE ADAPT THE TRACK INSTEAD OF THE PAGE DOING IT
 * -----------------------------------------------------
 * Below 1024 an auth form is built from the mobile `ds-*` atoms; at and above
 * it, from the desktop `dsk-*` primitives. That is a real difference, both
 * tracks exist and neither is a media query away from the other. But it is the
 * SAME difference on all nine routes, so if each page branched itself we would
 * write that branch nine times and get it subtly wrong somewhere.
 *
 * So the branch is here, once. A page writes `<AuthField label="Email">` and
 * gets the right track's control. Nothing is rendered twice and nothing is
 * hidden with CSS: `useViewport()` resolves to one track and only that track
 * mounts.
 *
 * Everything in this file is presentational. No fetch, no navigate, no toast.
 */

/* ------------------------------------------------------------------ error */

/** An inline error line: icon plus literal text, so colour is never alone. */
export function AuthError({ children, className, ...rest }) {
  if (!children) return null;
  return (
    <span className={cn('dsa-error', className)} role="alert" {...rest}>
      <Icon as={WarningCircle} size="sm" className="dsa-error__icon" />
      <span>{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ field */

/** The field stack. Every auth form's controls live inside exactly one. */
export function AuthFields({ className, ...rest }) {
  return <div className={cn('dsa-auth__fields', className)} {...rest} />;
}

/**
 * One labelled control.
 *
 * `foot` is the row under the control, for the case where an error and an
 * escape hatch share a line. Login is the reason it exists: "Incorrect
 * password" and "Forgot password?" belong beside each other, because the error
 * is precisely when the link becomes the useful thing on the page.
 */
export const AuthField = React.forwardRef(function AuthField(
  { label, hint, error, foot, id, children, className, ...rest },
  ref,
) {
  const { isDesktop } = useViewport();
  const uid = React.useId();
  const controlId = id || `${uid}-control`;
  const errorId = `${uid}-error`;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id: controlId,
        error: children.props.error != null ? children.props.error : Boolean(error),
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : children.props['aria-describedby'],
      })
    : children;

  // The desktop track has a real Field primitive with label association and
  // aria wiring already in it, so use it rather than reimplementing it. The
  // mobile track's Field has no error slot, so the scaffolding is drawn here.
  // `className` is forwarded on this branch too. It was previously dropped, and
  // the modal-form pattern needs `.dsk-field--full` to reach the real field so a
  // full-width row can span a two-up grid. No auth route passes one, so this is
  // additive.
  if (isDesktop && !foot) {
    return (
      <DskField
        ref={ref}
        label={label}
        hint={hint}
        error={error}
        htmlFor={controlId}
        className={className}
        {...rest}
      >
        {control}
      </DskField>
    );
  }

  return (
    <div ref={ref} className={cn(isDesktop ? 'dsk-field' : 'ds-field', className)} {...rest}>
      {label ? (
        <label className={isDesktop ? 'dsk-label' : 'ds-field__label'} htmlFor={controlId}>
          {label}
        </label>
      ) : null}
      {control}
      {hint ? <span className={isDesktop ? 'dsk-hint' : 'ds-field__hint'}>{hint}</span> : null}
      {foot ? (
        <div className="dsa-fieldfoot">
          <AuthError id={errorId}>{error}</AuthError>
          {foot}
        </div>
      ) : (
        <AuthError id={errorId}>{error}</AuthError>
      )}
    </div>
  );
});

/** The track's text input, so a page never picks one. */
export const AuthInput = React.forwardRef(function AuthInput(props, ref) {
  const { isDesktop } = useViewport();
  const Control = isDesktop ? DskInput : DsInput;
  return <Control ref={ref} {...props} />;
});

/**
 * A password input with the reveal control.
 *
 * The glyph follows the system's weight rule exactly: regular eye while the
 * password is hidden, FILLED eye-slash while it is revealed. Fill is the ON
 * weight, so the button's own state is legible without reading its label.
 */
export const AuthPasswordInput = React.forwardRef(function AuthPasswordInput(
  { disabled, className, ...rest },
  ref,
) {
  const [revealed, setRevealed] = React.useState(false);

  return (
    <span className="dsa-affix">
      <AuthInput
        ref={ref}
        type={revealed ? 'text' : 'password'}
        disabled={disabled}
        className={className}
        {...rest}
      />
      <button
        type="button"
        className="dsa-reveal"
        disabled={disabled}
        aria-pressed={revealed}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        onClick={() => setRevealed((value) => !value)}
      >
        <Icon as={revealed ? EyeSlash : Eye} size="sm" active={revealed} />
      </button>
    </span>
  );
});

/** The right-pinned quiet link in a field foot, or anywhere it reads as one. */
export function AuthGhostLink({ to, onClick, children, className, ...rest }) {
  if (to) {
    return (
      <Link className={cn('dsa-linkghost', className)} to={to} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={cn('dsa-linkghost', className)} onClick={onClick} {...rest}>
      {children}
    </button>
  );
}

/* ----------------------------------------------------------------- banner */

/**
 * A form-level message. `useAuth()` hands the pages a top level `error` and
 * `message` that belong to the submission rather than to any one field, and
 * this is where they go.
 */
export function AuthBanner({ tone = 'neutral', children, className, ...rest }) {
  if (!children) return null;
  const glyph = { error: WarningCircle, success: CheckCircle, neutral: Info }[tone] || Info;
  return (
    <div
      className={cn('dsa-banner', tone !== 'neutral' && `dsa-banner--${tone}`, className)}
      role={tone === 'error' ? 'alert' : 'status'}
      {...rest}
    >
      <Icon as={glyph} size="sm" className="dsa-banner__icon" />
      <span>{children}</span>
    </div>
  );
}

/* ---------------------------------------------------------------- commands */

/**
 * The submit. Block, large, ink, and the only primary command on the page.
 *
 * `loading` disables it and swaps the label, because a submit that stays live
 * during a request is how a user creates two accounts. Disabled is the real
 * attribute so the flat grey comes from the CSS, never from opacity.
 */
export function AuthSubmit({ loading = false, loadingLabel, disabled, children, ...rest }) {
  const { isDesktop } = useViewport();
  const label = loading ? loadingLabel || children : children;

  return (
    <div className="dsa-auth__submit">
      {isDesktop ? (
        <DskButton type="submit" variant="primary" size="lg" block disabled={disabled || loading} {...rest}>
          {label}
        </DskButton>
      ) : (
        <Cta type="submit" variant="solid" size="lg" block disabled={disabled || loading} {...rest}>
          {label}
        </Cta>
      )}
    </div>
  );
}

/** The one line under the rule: "New to Sleevr? Create an account". */
export function AuthFootnote({ children, className, ...rest }) {
  return (
    <p className={cn('dsa-auth__footnote', className)} {...rest}>
      {children}
    </p>
  );
}

/** A quiet link under the subtitle, for "use a different email address". */
export function AuthChangeLink({ to, onClick, icon = PencilSimple, children, ...rest }) {
  const content = (
    <>
      <Icon as={icon} size="sm" />
      {children}
    </>
  );
  if (to) {
    return (
      <Link className="dsa-changelink" to={to} {...rest}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" className="dsa-changelink" onClick={onClick} {...rest}>
      {content}
    </button>
  );
}

/**
 * The consent row: one real checkbox, and a label that carries the links.
 *
 * Promoted here rather than left with signup because the shape repeats. Signup
 * has it today and seller registration has the same "I agree to X and Y" row,
 * and the alternative is two page-local stylesheets drawing the same 11px gap.
 *
 * It is a real `<input type="checkbox">` inside a real `<label>`, so the whole
 * sentence is the hit target and the form semantics come from the platform. The
 * error sits under the row, never beside it, because the sentence already fills
 * the line.
 */
export function AuthConsent({ error, children, className, id, ...rest }) {
  const { isDesktop } = useViewport();
  const Check = isDesktop ? DskCheckbox : DsCheckbox;
  const uid = React.useId();
  const controlId = id || `${uid}-consent`;

  return (
    <div className={cn('dsa-consent', className)}>
      <div className="dsa-consent__row">
        <Check id={controlId} aria-invalid={error ? true : undefined} {...rest} />
        <label className="dsa-consent__text" htmlFor={controlId}>
          {children}
        </label>
      </div>
      <AuthError>{error}</AuthError>
    </div>
  );
}

/** The quiet advisory paragraph, for "check your spam folder". */
export function AuthHint({ children, className, ...rest }) {
  return (
    <p className={cn('dsa-hint', className)} {...rest}>
      <Icon as={Info} size="sm" className="dsa-hint__icon" />
      <span>{children}</span>
    </p>
  );
}

/* -------------------------------------------------------------------- OTP */

/**
 * The 6-digit code group.
 *
 * Below 1024 this is the mobile track's `OtpCells` molecule. At and above it,
 * desktop cells drawn here, because the desktop track has no OTP component
 * upstream and inventing one in the vendored layer would not survive a re-sync.
 *
 * Both share the same rules, and they are the ones that make OTP work:
 *
 * - NO `maxlength`. It truncates before the input event, so an autofilled
 *   6-digit code loses five digits silently. Any multi-character arrival is
 *   distributed across the cells instead, which is what autofill, iOS "From
 *   Messages" and Android keyboards all produce.
 * - `autocomplete="one-time-code"` on the FIRST cell only.
 * - One `role="group"` with a name, so six inputs do not read as six unrelated
 *   one-character fields, and each cell says which digit it is.
 * - The message region is always in the DOM, empty when silent.
 */
export function AuthOtp({
  length = 6,
  values,
  onChange,
  error,
  disabled = false,
  label = '6-digit verification code',
}) {
  const { isDesktop } = useViewport();
  const uid = React.useId();
  const legendId = `${uid}-legend`;
  const msgId = `${uid}-msg`;
  const refs = React.useRef([]);

  function distribute(from, raw) {
    const clean = String(raw).replace(/[^0-9]/g, '').slice(0, length - from);
    if (!clean) return;
    const next = [...values];
    for (let i = 0; i < clean.length; i += 1) next[from + i] = clean[i];
    onChange(next);
    const landed = Math.min(from + clean.length, length - 1);
    if (refs.current[landed]) refs.current[landed].focus();
  }

  function handleChange(index, raw) {
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.length > 1) {
      distribute(index, digits);
      return;
    }
    const next = [...values];
    next[index] = digits.slice(-1);
    onChange(next);
    if (digits && index < length - 1 && refs.current[index + 1]) refs.current[index + 1].focus();
  }

  function handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !values[index] && index > 0) {
      refs.current[index - 1]?.focus();
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  return (
    <div
      className={cn('dsa-otp', error && 'is-error')}
      role="group"
      aria-labelledby={legendId}
      aria-describedby={msgId}
    >
      <span className="dsk-sr-only" id={legendId}>
        {label}
      </span>

      {isDesktop ? (
        <div className="dsa-otp__cells">
          {Array.from({ length }).map((unused, index) => (
            <React.Fragment key={index}>
              {index === length / 2 ? <span className="dsa-otp__gap" aria-hidden="true" /> : null}
              <input
                ref={(element) => {
                  refs.current[index] = element;
                }}
                className={cn('dsa-otp__cell', values[index] && 'is-filled')}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                disabled={disabled}
                aria-label={`Digit ${index + 1} of ${length}`}
                aria-invalid={error ? true : undefined}
                value={values[index] || ''}
                // Typing REPLACES the digit already in a cell. Without the select,
                // that keystroke arrives as a two-character value, `distribute`
                // correctly fans it forward, and a correction quietly overwrites
                // every digit to its right.
                onFocus={(event) => event.target.select()}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                onPaste={(event) => {
                  event.preventDefault();
                  distribute(index, event.clipboardData.getData('text'));
                }}
              />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <OtpCells length={length} values={values} onChange={onChange} disabled={disabled} />
      )}

      <p className="dsa-otp__msg" id={msgId} role="status" aria-live="polite">
        {error ? (
          <>
            <Icon as={WarningCircle} size="sm" className="dsa-error__icon" />
            <span>{error}</span>
          </>
        ) : null}
      </p>
    </div>
  );
}

/**
 * Resend behind a cooldown.
 *
 * An always-live resend is how a user ends up with four codes in their inbox
 * and no idea which one is current, so the button is disabled until the
 * countdown clears. The per-second number is decoration and stays out of the
 * live region; only the milestones are announced.
 */
export function AuthResend({ seconds = 0, onResend, disabled = false }) {
  const announce =
    seconds === 0 ? 'You can request a new code now' : seconds === 30 || seconds === 10 ? `New code available in ${seconds} seconds` : '';

  return (
    <>
      <div className="dsa-resend">
        <button
          type="button"
          className="dsa-resend__btn"
          onClick={onResend}
          disabled={disabled || seconds > 0}
        >
          <Icon as={ArrowClockwise} size="sm" />
          Resend code
        </button>
        {seconds > 0 ? <span aria-hidden="true">in {seconds}s</span> : null}
      </div>
      <p className="dsk-sr-only" aria-live="polite">
        {announce}
      </p>
    </>
  );
}

/* ------------------------------------------------------------- strength */

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

/**
 * Password strength, four segments plus a word.
 *
 * Ink filled, never a red-amber-green traffic light. The meter is progress and
 * progress is not an outcome, and the word under it is what actually tells the
 * user what to do. It is advisory: it never blocks a submit on its own.
 */
export function AuthStrength({ value = '' }) {
  const score = React.useMemo(() => {
    if (!value || value.length < 6) return 0;
    let points = 1;
    if (value.length >= 10) points += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) points += 1;
    if (/[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)) points += 1;
    return Math.min(points, 4);
  }, [value]);

  return (
    <div className="dsa-strength">
      <div className="dsa-strength__track" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className={cn('dsa-strength__seg', index < score && 'is-on')} />
        ))}
      </div>
      <span className="dsa-strength__label">Password strength: {STRENGTH_LABELS[score]}</span>
    </div>
  );
}

/** Re-exported so a page imports its back link arrow from one place. */
export { ArrowLeft };
